import type {
  MetricsBySede,
  TotalByRiskLevelWithPercentage,
} from '@/domains/home/home.entity';
import type { Day } from '@/domains/shared';
import {
  buildTotalByRiskLevelWithPercentage,
  reduceTotalsByRiskLevel,
} from '@/shared/utils';

/**
 * Use Case para calcular métricas de riesgo agrupadas por jornada
 * Cumple SRP: Solo se encarga de agregar y calcular métricas por jornada
 *
 * Responsabilidades:
 * - Filtrar métricas por jornada específica (diurno/vespertino)
 * - Sumar totales de estudiantes por nivel de riesgo
 * - Calcular porcentajes de riesgo sobre el total de riesgos (no sobre estudiantes)
 * - Validar integridad de datos de entrada
 *
 * Encapsula la lógica de negocio de agregación y cálculo de porcentajes
 * Los porcentajes siempre suman 100% porque se calculan sobre la suma total de riesgos
 */
export class CalculateRiskByDayUseCase {
  /**
   * Calcula totales y porcentajes de riesgo para una jornada específica
   * Agrega métricas de todas las sedes para la jornada seleccionada
   * Los porcentajes representan la distribución relativa entre niveles de riesgo
   * @param data - Array de métricas por sede que contienen datos de riesgo por jornada
   * @param day - Jornada a filtrar ('V' para vespertino, 'D' para diurno)
   * @returns Objeto con totales y porcentajes de riesgo (alto, medio, bajo) que suman 100%
   * @throws Error si los datos no son válidos o la jornada es inválida
   */
  execute(data: MetricsBySede[], day: Day): TotalByRiskLevelWithPercentage {
    if (!Array.isArray(data)) {
      throw new Error('Los datos deben ser un array válido');
    }

    if (!day || !['V', 'D'].includes(day)) {
      throw new Error('La jornada debe ser "V" o "D"');
    }

    const filteredData = data.filter((item) => item.jornada === day);

    const totals = reduceTotalsByRiskLevel(filteredData);

    return buildTotalByRiskLevelWithPercentage(totals);
  }
}
